import type { ForwardedRef } from 'react';
import styled from '@emotion/styled';
import * as tokens from '@tokens';

import type { LinkProps } from '@components/Link/Link';
import { Link } from '@components/Link/Link';

export interface BreadcrumbProps<T extends string | undefined> {
  /**
   * Breadcrumb text.
   */
  children: string;
  /**
   * Styles override.
   */
  className?: string;
  /**
   * Link address.
   */
  href?: T;
  /**
   * Indicates whether breadcrumb represents the current page.
   * This will add the `aria-current="page"` attribute to the item as required by
   * the W3C accessibility guidelines.
   */
  isCurrent?: boolean;
  /**
   * onClick callback for the breadcrumb link.
   */
  onClick?: T extends string ? LinkProps<'a'>['onClick'] : undefined;
  /**
   * Component reference
   */
  ref?: ForwardedRef<HTMLLIElement>;
}

const Crumb = styled('li')`
  display: inline;
  position: relative;
  color: ${tokens.color.text_light};

  &::before {
    content: '/';
    display: inline-block;
    width: ${tokens.spacer.x1};
    margin: 0 ${tokens.spacer.x1};
    pointer-events: none;
    text-align: center;
  }

  &:hover::before {
    color: ${tokens.color.text_light};
  }

  &:first-of-type::before {
    display: none;
  }
`;

/**
 * Breadcrumb component to be used inside Breadcrumbs.
 * If `href` is provided, Breadcrumb will render as a link.
 *
 * {@link https://zeroheight.com/76c439891/p/58bd14-breadcrumbs Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-breadcrumbs Code documentation}
 *
 * @example
 * <Breadcrumbs.Item href="example.com">Breadcrumb Text</Breadcrumbs.Item>
 */
export const Breadcrumb = <T extends string | undefined>({
  children,
  className,
  href,
  isCurrent,
  onClick,
  ref,
}: BreadcrumbProps<T>): JSX.Element => (
  <Crumb
    className={className}
    ref={ref}
    {...(isCurrent ? { 'aria-current': 'page' } : {})}
  >
    {href ? (
      <Link href={href} kind="inline" onClick={onClick}>
        {children}
      </Link>
    ) : (
      children
    )}
  </Crumb>
);
